'use client';

import { useEffect, useState } from 'react';
import { Smartphone, X } from 'lucide-react';
import { useAddToHomeScreen } from '@/hooks/useAddToHomeScreen';
import { AddToHomeScreenModal } from '@/components/customer-surface/AddToHomeScreenModal';

/** A quiet, explicit entry to installation; never interrupts an order. */
export function PlaceInstallBanner({ businessName, businessKey }: { businessName: string; businessKey: string }) {
  const { installed, platform } = useAddToHomeScreen();
  const [dismissed, setDismissed] = useState(true);
  const [open, setOpen] = useState(false);
  const storageKey = `mnrq:install-banner:${businessKey}`;

  useEffect(() => {
    try {
      const until = Number(localStorage.getItem(storageKey));
      setDismissed(until > Date.now());
    } catch {
      setDismissed(false);
    }
  }, [storageKey]);

  if (installed || platform.kind === 'installed' || platform.kind === 'unsupported' || dismissed) return null;

  return (
    <aside aria-label="Save this place" className="border-b border-border bg-background text-foreground">
      <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 py-1">
        <Smartphone className="size-5 shrink-0" aria-hidden="true" />
        <p className="min-w-0 flex-1 text-xs leading-5">
          <span className="font-semibold">{businessName}, one tap away.</span>{' '}
          Add to your {platform.kind === 'desktop-safari' ? 'Dock' : 'home screen'}.
        </p>
        <button type="button" onClick={() => setOpen(true)} className="min-h-11 shrink-0 rounded-full px-3 text-sm font-semibold underline underline-offset-4">
          How to add
        </button>
        <button
          type="button"
          aria-label="Dismiss home screen banner"
          className="flex size-11 shrink-0 items-center justify-center rounded-full"
          onClick={() => {
            setDismissed(true);
            try {
              localStorage.setItem(storageKey, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
            } catch { /* Optional preference. */ }
          }}
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
      <AddToHomeScreenModal open={open} onOpenChange={setOpen} appName={businessName} />
    </aside>
  );
}
